import React, { useState, useEffect } from 'react';
import { Settings, Plus, Trash2, Star, ArrowLeft, Check, X, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { COUNTRIES, groceryStoresByCountry, type UserGroceryStore } from '../types/recipe';
import { useGroceryStore } from '../store/groceryStore';
import { AddCustomStore } from './AddCustomStore';
import { useSubscriptionStore } from '../store/subscriptionStore';

interface UserAccountProps {
  onBack: () => void;
}

export function UserAccount({ onBack }: UserAccountProps) {
  const { stores, loading, error, fetchStores, setDefaultStore } = useGroceryStore();
  const { isPremium, loading: subscriptionLoading, error: subscriptionError, upgrade } = useSubscriptionStore();
  const [selectedCountry, setSelectedCountry] = useState<keyof typeof COUNTRIES>('US');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleImageError = (storeId: string) => {
    setFailedImages(prev => new Set([...prev, storeId]));
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      await upgrade();
    } catch (err) {
      console.error('Failed to upgrade:', err);
    } finally {
      setUpgrading(false);
    }
  };

  const handleAddStore = async (storeId: string) => {
    setAddLoading(true);
    setAddError(null);

    try {
      const store = groceryStoresByCountry[selectedCountry].find(s => s.id === storeId);
      if (!store) throw new Error('Store not found');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_grocery_stores')
        .insert({
          user_id: user.id,
          store_id: store.id,
          store_name: store.name,
          search_url: store.searchUrl,
          logo_url: store.logo,
          country: store.country,
          is_default: stores.length === 0 // First store becomes default
        });

      if (error) throw error;
      await fetchStores();
    } catch (err) {
      console.error('Error adding store:', err);
      setAddError(err instanceof Error ? err.message : 'Failed to add store');
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddCustomStore = async (store: {
    store_id: string;
    store_name: string;
    search_url: string;
    logo_url: string | null;
    country: string;
  }) => {
    setAddLoading(true);
    setAddError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_grocery_stores')
        .insert({
          user_id: user.id,
          store_id: store.store_id,
          store_name: store.store_name,
          search_url: store.search_url,
          logo_url: store.logo_url,
          country: store.country,
          is_default: stores.length === 0 // First store becomes default
        });

      if (error) throw error;
      await fetchStores();
      setShowAddCustom(false);
    } catch (err) {
      console.error('Error adding custom store:', err);
      setAddError(err instanceof Error ? err.message : 'Failed to add store');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveStore = async (storeId: string) => {
    try {
      const { error } = await supabase
        .from('user_grocery_stores')
        .delete()
        .eq('store_id', storeId);

      if (error) throw error;
      await fetchStores();
    } catch (err) {
      console.error('Error removing store:', err);
    }
  };

  const renderStoreIcon = (store: { store_id: string; store_name: string; logo_url: string | null }) => {
    if (failedImages.has(store.store_id) || !store.logo_url) {
      return (
        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
          <span className="text-gray-500 text-xs font-medium">
            {store.store_name.charAt(0)}
          </span>
        </div>
      );
    }

    return (
      <img
        src={store.logo_url}
        alt={store.store_name}
        className="w-6 h-6 object-contain"
        onError={() => handleImageError(store.store_id)}
      />
    );
  };

  // Separate stores by type
  const supermarkets = groceryStoresByCountry[selectedCountry].filter(store => store.type === 'supermarket');
  const deliveryServices = groceryStoresByCountry[selectedCountry].filter(store => store.type === 'delivery');

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Back to recipes"
        >
          <ArrowLeft size={24} className="text-gray-600" />
        </button>
        <Settings size={24} className="text-gray-600" />
        <h1 className="text-2xl font-bold">Account Settings</h1>
      </div>

      <div className="space-y-6">
        {/* Premium Status Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className={isPremium ? "text-yellow-500" : "text-gray-400"} size={24} />
              <div>
                <h2 className="text-xl font-semibold">Premium Status</h2>
                <p className="text-gray-600 mt-1">
                  {isPremium ? 'You have access to all premium features!' : 'Upgrade to access premium features'}
                </p>
              </div>
            </div>
            {!isPremium && (
              <button
                onClick={handleUpgrade}
                disabled={upgrading || subscriptionLoading}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Sparkles size={20} />
                {upgrading ? 'Upgrading...' : 'Upgrade Now'}
              </button>
            )}
          </div>

          {(subscriptionError || error || addError) && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {subscriptionError || error || addError}
            </div>
          )}

          {!isPremium && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium mb-2">Free Features</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    Basic recipe management
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    Up to 3 grocery stores
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    Basic meal planning
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <h3 className="font-medium mb-2">Premium Features</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <Sparkles size={16} className="text-purple-500" />
                    AI recipe enhancement
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles size={16} className="text-purple-500" />
                    Unlimited grocery stores
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles size={16} className="text-purple-500" />
                    Advanced meal planning
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Grocery Stores Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-6">Grocery Stores</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-3">Your Stores</h3>
              {stores.length === 0 ? (
                <p className="text-gray-500 italic">No stores added yet. Add some stores below!</p>
              ) : (
                <div className="space-y-2">
                  {stores.map((store) => (
                    <div
                      key={store.store_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group"
                    >
                      <div className="flex items-center gap-3">
                        {renderStoreIcon(store)}
                        <span className="font-medium">{store.store_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setDefaultStore(store.store_id)}
                          disabled={loading}
                          className={`p-2 rounded-lg transition-colors ${
                            store.is_default
                              ? 'text-yellow-500'
                              : 'text-gray-400 opacity-0 group-hover:opacity-100 hover:text-yellow-500 hover:bg-gray-100'
                          }`}
                          title={store.is_default ? 'Default store' : 'Set as default'}
                        >
                          <Star
                            size={20}
                            fill={store.is_default ? 'currentColor' : 'none'}
                          />
                        </button>
                        <button
                          onClick={() => handleRemoveStore(store.store_id)}
                          disabled={loading}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100"
                          title="Remove store"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {showAddCustom ? (
              <AddCustomStore
                onAdd={handleAddCustomStore}
                onCancel={() => setShowAddCustom(false)}
              />
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">Add Stores</h3>
                  <button
                    onClick={() => setShowAddCustom(true)}
                    disabled={!isPremium && stores.length >= 3}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                    Add Custom Store
                  </button>
                </div>

                {/* {!isPremium && stores.length >= 3 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700 flex items-center gap-2">
                    <Sparkles size={16} />
                    Upgrade to premium to add more stores!
                  </div>
                )}
 */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Country
                    </label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value as keyof typeof COUNTRIES)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(COUNTRIES).map(([code, name]) => (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-6">
                    {/* Supermarkets Section */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Supermarkets</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {supermarkets.map((store) => {
                          const isAdded = stores.some(s => s.store_id === store.id);
                          const disabled = (!isPremium && false) && !isAdded && stores.length >= 3; //TODO remove the false to bring back premium
                          return (
                            <button
                              key={store.id}
                              onClick={() => isAdded ? handleRemoveStore(store.id) : handleAddStore(store.id)}
                              disabled={addLoading || disabled}
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                isAdded
                                  ? 'bg-gray-50 border-gray-200'
                                  : disabled
                                  ? 'opacity-50 cursor-not-allowed border-gray-200'
                                  : 'hover:bg-gray-50 border-gray-300'
                              }`}
                            >
                              {renderStoreIcon({
                                store_id: store.id,
                                store_name: store.name,
                                logo_url: store.logo
                              })}
                              <span className="flex-1 text-left font-medium">
                                {store.name}
                              </span>
                              {isAdded ? (
                                <Check size={20} className="text-green-600" />
                              ) : (
                                <Plus size={20} className="text-gray-600" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Delivery Services Section */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Delivery Services</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {deliveryServices.map((store) => {
                          const isAdded = stores.some(s => s.store_id === store.id);
                          const disabled = !isPremium && !isAdded && stores.length >= 3;
                          return (
                            <button
                              key={store.id}
                              onClick={() => isAdded ? handleRemoveStore(store.id) : handleAddStore(store.id)}
                              disabled={addLoading || disabled}
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                isAdded
                                  ? 'bg-gray-50 border-gray-200'
                                  : disabled
                                  ? 'opacity-50 cursor-not-allowed border-gray-200'
                                  : 'hover:bg-gray-50 border-gray-300'
                              }`}
                            >
                              {renderStoreIcon({
                                store_id: store.id,
                                store_name: store.name,
                                logo_url: store.logo
                              })}
                              <span className="flex-1 text-left font-medium">
                                {store.name}
                              </span>
                              {isAdded ? (
                                <Check size={20} className="text-green-600" />
                              ) : (
                                <Plus size={20} className="text-gray-600" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}